import React, { useEffect, useState } from 'react'
import { Button, Select, Space, Tag, Typography } from 'antd'
import { ReloadOutlined, WhatsAppOutlined, PhoneOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'

import {
  carRequestsUrl,
  getRequest,
  patchRequest,
  errorNotification,
  successNotification
} from '../../config'
import PageLoader from '../../components/Layouts/PageLoader'
import { STATUS_OPTIONS, statusColor } from './tableColumns'

const { Text, Paragraph } = Typography

let nf = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

/**
 * One car request, rendered the same way whether it is a page or the list's quick view.
 *
 * Fetches by id rather than taking the row the table already holds: the list may have been open a
 * while, and status is the one field two people can change at once. Paired with `destroyOnClose` on
 * the modal, every open shows the current state.
 */
const RequestBody = props => {
  const { id, mode, onUpdated } = props
  const asModal = mode === 'modal'

  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const getDetail = async () => {
    if (!id) return
    const response = await getRequest(`${carRequestsUrl}/${id}/`, 'car request', setLoading)
    if (response.success) {
      setRequest(response.data)
    } else if (response.error.response) {
      errorNotification(response.error.response.data.message)
    }
  }

  useEffect(() => {
    getDetail()
    // eslint-disable-next-line
  }, [id])

  const changeStatus = async status => {
    const previous = request.status
    // Optimistic: the select should not sit on the old value while a round trip finishes.
    setRequest({ ...request, status })
    const response = await patchRequest(
      `${carRequestsUrl}/${id}/`,
      { status },
      'status',
      setSaving
    )
    if (response.success) {
      successNotification('Status updated')
      if (onUpdated) onUpdated({ ...request, status })
    } else {
      // Revert visibly. A status that silently did not save means two people call the same
      // customer, which is the exact thing this field exists to prevent.
      setRequest({ ...request, status: previous })
      errorNotification('Could not update the status')
    }
  }

  const digits = (request?.phone || '').replace(/\D/g, '')

  return (
    <PageLoader className={asModal ? '' : 'container-fluid'} loading={loading}>
      {request ? (
        <div className={asModal ? '' : 'card mb-4rem'}>
          {!asModal && <div className="card-header">Car Request</div>}
          <div className={asModal ? '' : 'card-body'}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '1rem'
              }}
            >
              <Space size={8}>
                <Tag color={statusColor(request.status)}>
                  {String(request.status).toUpperCase()}
                </Tag>
                <Text type="secondary">
                  {new Date(request.createdAt).toDateString()}
                </Text>
              </Space>
              <Space size={4}>
                <Select
                  size="small"
                  value={request.status}
                  loading={saving}
                  onChange={changeStatus}
                  options={STATUS_OPTIONS}
                  style={{ width: 120 }}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={getDetail}>
                  Refresh
                </Button>
              </Space>
            </div>

            <div className="mb-2rem">
              <div>
                <b>Ready budget</b>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>
                GH₵ {nf.format(request.budgetGhs)}
              </div>
            </div>

            <div className="mb-2rem">
              <div>
                <b>What they asked for</b>
              </div>
              {/* The whole thing, never truncated: the table already shows an excerpt, and this is
                  the screen someone opens precisely to read the rest. */}
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {request.description}
              </Paragraph>
            </div>

            <div className="mb-2rem">
              <div>
                <b>Customer</b>
              </div>
              <Space size={8} wrap>
                <Text copyable>{request.phone}</Text>
                <Button
                  size="small"
                  icon={<WhatsAppOutlined />}
                  href={`https://wa.me/${digits}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </Button>
                <Button size="small" icon={<PhoneOutlined />} href={`tel:+${digits}`}>
                  Call
                </Button>
              </Space>
            </div>
          </div>
        </div>
      ) : null}
    </PageLoader>
  )
}

RequestBody.propTypes = {
  /** Which request. */
  id: PropTypes.string,
  /** 'page' (default, routed) or 'modal' (the list's quick view). */
  mode: PropTypes.oneOf(['page', 'modal']),
  /** Lets the list update the row behind the modal without refetching the page. */
  onUpdated: PropTypes.func
}

RequestBody.defaultProps = { mode: 'page' }

export default RequestBody
